import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Phone, Mail, MapPin, MessageCircle, Clock, Calendar, Send, User, Baby, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { childcareData } from "../data/mock";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    parentName: "",
    email: "",
    phone: "",
    childName: "",
    childAge: "",
    preferredDays: [],
    dayTimings: {
      Monday: "",
      Tuesday: "",
      Wednesday: "",
      Thursday: "",
      Friday: ""
    },
    careType: "", // "term-time" or "all-year"
    startDate: "",
    tourDate: "",
    message: "",
    hearAboutUs: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const daysOfWeek = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"
  ];

  const timeOptions = [
    { value: "morning", label: "Morning (8:30am - 12:30pm)" },
    { value: "afternoon", label: "Afternoon (12:30pm - 4:30pm)" },
    { value: "full", label: "Full Day (8:30am - 4:30pm)" },
    { value: "flexible", label: "Flexible (please specify in message below)" }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const newPreferredDays = prev.preferredDays.includes(day)
        ? prev.preferredDays.filter(d => d !== day)
        : [...prev.preferredDays, day];
      
      // Clear timing for days that are unchecked
      const newDayTimings = { ...prev.dayTimings };
      if (!newPreferredDays.includes(day)) {
        newDayTimings[day] = "";
      }
      
      return {
        ...prev,
        preferredDays: newPreferredDays,
        dayTimings: newDayTimings
      };
    });
  };

  const handleDayTimingChange = (day, timing) => {
    setFormData(prev => ({
      ...prev,
      dayTimings: {
        ...prev.dayTimings,
        [day]: timing
      }
    }));
  };

  const validateForm = () => {
    const required = ['parentName', 'email', 'phone', 'childName', 'childAge'];
    for (const field of required) {
      if (!formData[field] || formData[field].trim() === '') {
        return `Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
      }
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "Please enter a valid email address";
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsSubmitting(true);
    setError("");

    try {
      // Prepare data for backend - convert dayTimings to a more readable format
      const submissionData = {
        ...formData,
        preferredTimes: formData.preferredDays.map(day => 
          formData.dayTimings[day] ? `${day}: ${timeOptions.find(t => t.value === formData.dayTimings[day])?.label || formData.dayTimings[day]}` : `${day}: Any time`
        ).join('; ')
      };

      const response = await axios.post(`${API}/contact/enquiry`, submissionData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });

      if (response.data.success) {
        setSubmitted(true);
        console.log("✅ Enquiry submitted successfully:", response.data);
      } else {
        throw new Error(response.data.error || "Failed to submit enquiry");
      }
      
    } catch (err) {
      console.error("❌ Error submitting enquiry:", err);
      
      if (err.code === 'ECONNABORTED') {
        setError("Request timeout. Please check your connection and try again.");
      } else if (err.response?.status === 500) {
        setError("Server error. Please try again or contact us directly.");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Sorry, there was an error sending your enquiry. Please try again or contact us directly.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = "Hello! I'm interested in learning more about Heathbourne House Childcare and booking a tour.";
    const whatsappUrl = `https://wa.me/${childcareData.contact.whatsapp.jasmin.replace('+', '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (submitted) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-20">
          <section className="py-20 bg-blue-50 min-h-screen flex items-center">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="bg-white rounded-3xl p-12 border border-pink-100 shadow-xl">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <h1 className="text-4xl font-bold text-slate-800 mb-6">Thank You!</h1>
                <p className="text-xl text-slate-600 mb-8">
                  Your enquiry has been sent successfully. We'll get back to you within 24 hours to discuss your childcare needs and arrange a tour.
                </p>
                <div className="bg-blue-50 rounded-2xl p-6 mb-8">
                  <h3 className="font-semibold text-slate-800 mb-2">What happens next?</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p>✅ Your enquiry has been received and saved</p>
                    <p>📧 Confirmation email sent to your inbox</p>
                    <p>📞 Liza or Jasmin will contact you within 24 hours</p>
                    <p>🏠 We'll arrange a convenient tour time</p>
                    <p>📋 Once you sign up, you will be sent a contract to complete</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={handleWhatsAppClick}
                    className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-full transition-all"
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    WhatsApp Us
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.location.href = "/"}
                    className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-3 rounded-full transition-all"
                  >
                    Return Home
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        
        {/* Hero Section */}
        <section className="py-20 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6">
                Book Your 
                <span style={{color: '#F8BBD9'}}> Tour</span>
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Ready to give your child the best start? Complete our intake form below and we'll get back to you within 24 hours to arrange your visit.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl p-8 border border-pink-100 sticky top-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-8">Contact Information</h2>
                
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Phone</h3>
                      <p className="text-slate-600">
                        <strong>Jasmin:</strong> {childcareData.contact.phones.primary}
                      </p>
                      <p className="text-slate-600">
                        <strong>Liza:</strong> {childcareData.contact.phones.secondary}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Email</h3>
                      <a 
                        href={`mailto:${childcareData.contact.email}`}
                        className="text-slate-600 hover:text-blue-500 transition-colors break-all text-sm"
                      >
                        {childcareData.contact.email}
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Location</h3>
                      <p className="text-slate-600">{childcareData.contact.address}</p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Hours</h3>
                      <p className="text-slate-600">Monday - Friday</p>
                      <p className="text-slate-600">8:30am - 4:30pm</p>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Button */}
                <Button 
                  onClick={handleWhatsAppClick}
                  className="w-full mt-8 bg-green-500 hover:bg-green-600 text-white py-3 rounded-full transition-all transform hover:scale-105"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Message on WhatsApp
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-8 border border-pink-100">
                <h2 className="text-3xl font-bold text-slate-800 mb-2">Intake Form & Tour Booking</h2>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours to arrange your visit.
                </p>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" id="contact-form">
                  
                  {/* Parent Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <User className="inline h-4 w-4 mr-1" />
                        Parent/Guardian Name *
                      </label>
                      <Input
                        type="text"
                        required
                        value={formData.parentName}
                        onChange={(e) => handleInputChange('parentName', e.target.value)}
                        className="w-full"
                        placeholder="Your full name"
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Mail className="inline h-4 w-4 mr-1" />
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full"
                        placeholder="your.email@example.com"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Phone className="inline h-4 w-4 mr-1" />
                        Phone Number *
                      </label>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full"
                        placeholder="+44 123 456 7890"
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        How did you hear about us?
                      </label>
                      <Select onValueChange={(value) => handleInputChange('hearAboutUs', value)} disabled={isSubmitting}>
                        <SelectTrigger>
                          <SelectValue placeholder="Please select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="google">Google Search</SelectItem>
                          <SelectItem value="facebook">Facebook</SelectItem>
                          <SelectItem value="instagram">Instagram</SelectItem>
                          <SelectItem value="friend">Friend/Family Referral</SelectItem>
                          <SelectItem value="local">Local Advertisement</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Child Information */}
                  <div className="border-t border-pink-100 pt-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-4">
                      <Baby className="inline h-5 w-5 mr-2" />
                      Child Information
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Child's Name *
                        </label>
                        <Input
                          type="text"
                          required
                          value={formData.childName}
                          onChange={(e) => handleInputChange('childName', e.target.value)}
                          className="w-full"
                          placeholder="Your child's name"
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Child's Age *
                        </label>
                        <Select onValueChange={(value) => handleInputChange('childAge', value)} disabled={isSubmitting}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select age..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 year old</SelectItem>
                            <SelectItem value="2">2 years old</SelectItem>
                            <SelectItem value="3">3 years old</SelectItem>
                            <SelectItem value="4">4 years old</SelectItem>
                            <SelectItem value="5">5 years old</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Care Preferences with Day-Specific Timings */}
                  <div className="border-t border-pink-100 pt-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-4">
                      <Calendar className="inline h-5 w-5 mr-2" />
                      Care Preferences
                    </h3>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          Preferred Days & Times (select days and specify times for each)
                        </label>
                        
                        <div className="space-y-4">
                          {daysOfWeek.map((day) => (
                            <div key={day} className="border border-slate-200 rounded-lg p-4">
                              <div className="flex items-center space-x-3 mb-3">
                                <Checkbox
                                  id={day}
                                  checked={formData.preferredDays.includes(day)}
                                  onCheckedChange={() => handleDayToggle(day)}
                                  disabled={isSubmitting}
                                />
                                <label htmlFor={day} className="text-sm font-medium text-slate-700 cursor-pointer">
                                  {day}
                                </label>
                              </div>
                              
                              {formData.preferredDays.includes(day) && (
                                <div className="ml-6">
                                  <Select 
                                    onValueChange={(value) => handleDayTimingChange(day, value)} 
                                    disabled={isSubmitting}
                                    value={formData.dayTimings[day]}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Select time for this day..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          <Calendar className="inline h-4 w-4 mr-1" />
                          Care Type *
                        </label>
                        <Select 
                          onValueChange={(value) => handleInputChange('careType', value)} 
                          disabled={isSubmitting}
                          required
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select care type..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all-year">All Year Round (except holiday closures)</SelectItem>
                            <SelectItem value="term-time">Term Time Only</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          All year round: Closed 2 weeks in August & 2 weeks at Christmas
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Preferred Start Date
                          </label>
                          <Input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => handleInputChange('startDate', e.target.value)}
                            className="w-full"
                            disabled={isSubmitting}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Preferred Tour Date
                          </label>
                          <Input
                            type="date"
                            value={formData.tourDate}
                            onChange={(e) => handleInputChange('tourDate', e.target.value)}
                            className="w-full"
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message */}  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Additional Message or Questions
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="w-full h-32"
                      placeholder="Tell us anything else you'd like us to know about your child's needs, any questions you have, or special requirements..."
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Submit Intake Form
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;